import { create } from 'zustand'
import type { User } from '@/types'
import * as api from '@/services/api'

interface AuthState {
  token: string | null
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  login: (username: string, password: string) => Promise<void>
  loginBySms: (phone: string, smsCode: string) => Promise<void>
  sendSmsCode: (phone: string, purpose?: string) => Promise<{ ok: boolean; msg?: string }>
  checkAuth: () => Promise<void>
  logout: () => void
}

export const useAuthStore = create<AuthState>((set) => ({
  token: localStorage.getItem('qd_token'),
  user: (() => {
    try {
      const u = localStorage.getItem('qd_user')
      return u ? JSON.parse(u) : null
    } catch { return null }
  })(),
  isAuthenticated: !!localStorage.getItem('qd_token'),
  isLoading: true,
  error: null,

  login: async (username, password) => {
    set({ isLoading: true, error: null })
    try {
      const res = await api.login(username, password)
      localStorage.setItem('qd_token', res.token)
      localStorage.setItem('qd_user', JSON.stringify(res.user))
      set({ token: res.token, user: res.user, isAuthenticated: true, isLoading: false })
    } catch (err) {
      set({ error: (err as Error).message, isLoading: false })
      throw err
    }
  },

  loginBySms: async (phone, smsCode) => {
    set({ isLoading: true, error: null })
    try {
      const res = await api.loginBySms(phone, smsCode)
      localStorage.setItem('qd_token', res.token)
      localStorage.setItem('qd_user', JSON.stringify(res.user))
      set({ token: res.token, user: res.user, isAuthenticated: true, isLoading: false })
    } catch (err) {
      set({ error: (err as Error).message, isLoading: false })
      throw err
    }
  },

  sendSmsCode: async (phone, purpose = 'sms_login') => {
    return api.sendSmsCode(phone, purpose)
  },

  checkAuth: async () => {
    const token = localStorage.getItem('qd_token')
    if (!token) {
      set({ isAuthenticated: false, isLoading: false })
      return
    }
    try {
      const user = await api.getUserInfo()
      localStorage.setItem('qd_user', JSON.stringify(user))
      set({ user, isAuthenticated: true, isLoading: false })
    } catch {
      localStorage.removeItem('qd_token')
      localStorage.removeItem('qd_user')
      set({ token: null, user: null, isAuthenticated: false, isLoading: false })
    }
  },

  logout: () => {
    localStorage.removeItem('qd_token')
    localStorage.removeItem('qd_user')
    set({ token: null, user: null, isAuthenticated: false })
  },
}))
